import { Link } from "react-router-dom";
import UpdateLive from "./UpdateLive";
import axios from "axios";
import { config } from "../../../../config";
import { useEffect, useState } from "react";

const ViewLive = () => {
  const [lives, setLives] = useState([]);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingLive, setEditingLive] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    slug: "",
    video: null,
    video_url: "",
    seenPeople: 0,
    offerHeadline: "",
    offerParagraph: "",
    offerButton: "",
    offerTime: "",
    redirectPage: "",
    pixelId: "",
    bonus_links: []
  });
  const apiUrl = config.apiUrl;
  const imageUrl = config.imageUrl;

  // Fetch lives from API
  useEffect(() => {
    const fetchLives = async () => {
      setTableLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/lives`);
        setLives(response.data.lives || response.data); // Adjust based on actual response structure
      } catch (err) {
        console.error("Error fetching lives:", err);
        setError("Failed to load live entries.");
      } finally {
        setLoading(false);
        setTableLoading(false);
      }
    };

    fetchLives();
  }, []);

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this live entry?")) {
      setTableLoading(true);
      try {
        await axios.delete(`${apiUrl}/livedelete/${id}`);
        setLives((prev) => prev.filter((live) => live.id !== id));
      } catch (err) {
        console.error("Error deleting live entry:", err);
        setError("Failed to delete live entry.");
      } finally {
        setTableLoading(false);
      }
    }
  };

  // Handle Edit Button Click
  const handleEditClick = (live) => {
    setEditingLive(live.id);
    setFormData({
      slug: live.slug,
      video: live.video,
      video_url: live.video,
      seenPeople: live.seenPeople,
      offerHeadline: live.offerHeadline,
      offerParagraph: live.offerParagraph,
      offerButton: live.offerButton,
      offerTime: live.offerTime,
      redirectPage: live.redirectPage,
      pixelId: live.pixelId || "",
      bonus_links: live.bonus_links || []
    });
  };

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle Bonus Link Changes
  const handleBonusLinkChange = (index, field, value) => {
    const updatedBonusLinks = [...formData.bonus_links];
    updatedBonusLinks[index][field] = value;
    setFormData((prev) => ({ ...prev, bonus_links: updatedBonusLinks }));
  };

  // Add Bonus Link
  const addBonusLink = () => {
    setFormData((prev) => ({
      ...prev,
      bonus_links: [
        ...prev.bonus_links,
        {
          type: "gift",
          title: "",
          button_text: "",
          link: "",
          description: "",
          show_time: 0,
          duration: 60,
          order: prev.bonus_links.length,
          is_active: 1
        }
      ]
    }));
  };

  // Remove Bonus Link
  const removeBonusLink = (index) => {
    const updatedBonusLinks = [...formData.bonus_links];
    updatedBonusLinks.splice(index, 1);
    setFormData((prev) => ({ ...prev, bonus_links: updatedBonusLinks }));
  };

  // Handle Form Submit with Progress Tracking
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!editingLive) {
      setError("No live entry selected for editing.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("_method", "PUT");
    data.append("slug", formData.slug);
    if (video) {
      data.append("video", video);
    } else {
      data.append("video_url", formData.video_url);
    }
    data.append("seenPeople", formData.seenPeople);
    data.append("offerHeadline", formData.offerHeadline);
    data.append("offerParagraph", formData.offerParagraph);
    data.append("offerButton", formData.offerButton);
    data.append("offerTime", formData.offerTime);
    data.append("redirectPage", formData.redirectPage);
    data.append("pixelId", formData.pixelId);

    // Bonus links যোগ করা
    formData.bonus_links.forEach((link, index) => {
      data.append(`bonus_links[${index}][type]`, link.type);
      data.append(`bonus_links[${index}][title]`, link.title);
      data.append(`bonus_links[${index}][button_text]`, link.button_text);
      data.append(`bonus_links[${index}][link]`, link.link);
      data.append(`bonus_links[${index}][description]`, link.description || "");
      data.append(`bonus_links[${index}][show_time]`, link.show_time);
      data.append(`bonus_links[${index}][duration]`, link.duration);
      data.append(`bonus_links[${index}][order]`, link.order);
      data.append(`bonus_links[${index}][is_active]`, link.is_active);
    });

    try {
      await axios.post(`${apiUrl}/livesupdate/${editingLive}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      // Refresh the list
      const response = await axios.get(`${apiUrl}/lives`);
      setLives(response.data.lives);
      
      setEditingLive(null);
      setUploadProgress(0);
      setError(null);
    } catch (err) {
      console.error("Error updating live entry:", err);
      setError("Failed to update live entry. " + (err.response?.data?.message || ""));
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  if (loading && lives.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Live Pages Management</h2>
        <Link
          to="/admin/lives/create"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Create New Live
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
          <p>{error}</p>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video Preview</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Viewers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Offer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bonus Links</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tableLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  </td>
                </tr>
              ) : lives.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No live pages found
                  </td>
                </tr>
              ) : (
                lives.map((live) => (
                  <tr key={live.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{live.slug}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {live.video && (
                        <video width="120" controls className="max-h-20">
                          <source src={live.video.startsWith('http') ? live.video : `${imageUrl}/storage/${live.video}`} type="video/mp4" />
                        </video>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{live.seenPeople}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{live.offerHeadline}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {live.bonus_links?.length || 0} links
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditClick(live)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(live.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                        <Link
                          to={`/live/${live.slug}`}
                          className="text-green-600 hover:text-green-900"
                          target="_blank"
                        >
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingLive && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <UpdateLive
                formData={formData}
                handleChange={handleChange}
                handleBonusLinkChange={handleBonusLinkChange}
                addBonusLink={addBonusLink}
                removeBonusLink={removeBonusLink}
                handleSubmit={handleSubmit}
                setVideo={setVideo}
                uploadProgress={uploadProgress}
                loading={loading}
                error={error}
                onCancel={() => setEditingLive(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewLive;